class VariantSelects extends HTMLElement {
  constructor () {
    super()
    this.optionEls = []
    this.optionNames = []
  }

  connectedCallback () {
    this.optionEls = [...this.querySelectorAll(this.optionSelector)]
    this.setOptionStates()
    this.addEventListener('change', this.onVariantChange)
  }

  getPossibleOptions (option, filters) {
    return [
      ...new Set(
        this.getVariantData()
          .filter(variant => filters.every(fn => fn(variant)))
          .map(variant => variant[option]),
      ),
    ]
  }

  getAvailableOptions (option, filters) {
    return [
      ...new Set(
        this.getVariantData()
          .filter(variant => variant.available)
          .filter(variant => filters.every(fn => fn(variant)))
          .map(variant => variant[option]),
      ),
    ]
  }

  getFilters (options, variantOptions) {
    return options.map((option, index) => {
      return variant =>
        options[index] && variant[variantOptions[index]] === options[index]
    })
  }

  getSelectedValues (options) {
    return options.map(option => {
      const el = this.optionEls.find(
        optionEl =>
          optionEl.dataset.index.includes(option) &&
          optionEl.getAttribute('aria-pressed') === 'true',
      )
      return el?.dataset?.value
    })
  }

  setOptionState (currentOption, optionsRequired) {
    const selectedValues = this.getSelectedValues(optionsRequired)
    const filters = this.getFilters(selectedValues, optionsRequired)
    const availableOptionValues = this.getAvailableOptions(
      currentOption,
      filters,
    )
    const possibleOptionValues = this.getPossibleOptions(currentOption, filters)

    this.optionEls
      .filter(optionEl =>
        optionEl.dataset.index.toString().includes(currentOption),
      )
      .forEach(optionEl => {
        if (!optionEl.dataset.value) return
        const isAvailable = availableOptionValues.find(
          value => value.toString() === optionEl.dataset.value.toString(),
        )
        const optionExists = possibleOptionValues.find(
          value => value.toString() === optionEl.dataset.value.toString(),
        )
        optionEl.setAttribute('data-available', (!!isAvailable).toString())

        if (!optionExists) {
          optionEl.setAttribute('disabled', '')
          optionEl.setAttribute('aria-disabled', true)
        } else {
          optionEl.removeAttribute('disabled')
          optionEl.removeAttribute('aria-disabled')
        }
      })
  }

  getOptionNames () {
    if (this.optionNames.length > 0) {
      return this.optionNames
    }

    const count = this.getVariantData().reduce((acc, variant) => {
      if (variant.options.length > acc) {
        acc = variant.options.length
      }

      return acc
    }, 0)

    this.optionNames = [...Array(count).keys()].map(n => `option${n + 1}`)

    return this.optionNames
  }

  setOptionStates () {
    this.getOptionNames().forEach((option, index, options) => {
      const current = options[index]
      const required = options.filter(item => item !== option)
      this.setOptionState(current, required)
    })
  }

  onVariantChange (event) {
    event.stopImmediatePropagation()
    this.updateOptions()
    this.updateMasterId()

    const isBundleVariantChange =
      event?.currentTarget?.dataset?.section === 'async-bundle-item'

    if (isBundleVariantChange) {
      this.dispatchChangeEvent(event)
      this.updateVariantInput()
    } else {
      if (!this.currentVariant) {
        this.setAvailability()
      } else {
        this.dispatchChangeEvent(event)
        this.updateMedia()
        this.updateURL()
        this.updateVariantInput()
        this.renderProductInfo()
        this.setAvailability()
        this.updateCurrentSizeValue(event.target)
      }
    }

    this.setOptionStates()
  }

  updateOptions () {
    this.options = Array.from(
      this.querySelectorAll('select'),
      select => select.value,
    )
  }

  updateMasterId () {
    this.currentVariant = this.getVariantData().find(variant => {
      return !variant.options
        .map((option, index) => {
          return this.options[index] === option
        })
        .includes(false)
    })
  }

  updateCurrentSizeValue (target) {
    if (target.dataset.index?.indexOf('option1') === -1) return

    const sizeValueEl = document.querySelector(
      '[data-variant-selects-current-size]',
    )
    if (!sizeValueEl) return
    sizeValueEl.innerText = target.value
  }

  dispatchChangeEvent (event) {
    window.BAO.dispatchEvent(window.BAO.EVENTS.PRODUCT.VARIANT_CHANGED, {
      product: {
        id: this.productId,
      },
      variant: {
        id: this.currentVariant.id,
        available: this.currentVariant.available,
      },
      triggeredBy: event,
    })
  }

  updateMedia () {
    if (!this.currentVariant || !this.currentVariant?.featured_media) return
    const newMedia = document.querySelector(
      `[data-media-id="${this.dataset.section}-${this.currentVariant.featured_media.id}"]`,
    )
    if (!newMedia) return
    const parent = newMedia.parentElement
    parent.prepend(newMedia)
    window.setTimeout(() => {
      parent.scroll(0, 0)
    })
  }

  updateURL () {
    if (!this.currentVariant || this.isQuickBuy || this.isBundleUpsell) {
      return
    }

    window.history.replaceState(
      {},
      '',
      `${this.dataset.url}?variant=${this.currentVariant.id}`,
    )
  }

  updateVariantInput () {
    const form = this.closest('product-form')
    const input = form.querySelector('input[name="id"]')
    input.value = this.currentVariant.id
    input.dispatchEvent(new Event('change', { bubbles: true }))
  }

  renderProductInfo () {
    fetch(
      `${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.dataset.section}`,
    )
      .then(response => response.text())
      .then(responseText => {
        const id = `price-${this.dataset.section}`
        const html = new DOMParser().parseFromString(responseText, 'text/html')
        const destination = document.getElementById(id)
        const source = html.getElementById(id)

        if (source && destination) destination.innerHTML = source.innerHTML

        // eslint-disable-next-line no-unused-expressions
        document
          .getElementById(`price-${this.dataset.section}`)
          ?.classList.remove('visibility-hidden')
      })
  }

  setAvailability () {
    let addToBagSelector = '[data-product-add-to-cart]'
    if (this.isQuickBuy) {
      addToBagSelector = '[data-product-quick-buy-add-to-cart]'
    } else if (this.isBundleUpsell) {
      addToBagSelector = `[data-product-bundle-upsell-add-to-cart][data-product-id="${this.productId}"]`
    }

    const addToBagButtons = window.BAO.utils.getElements(addToBagSelector)

    let addToBagTextsSelector = '[data-product-add-to-cart-text]'
    if (this.isQuickBuy) {
      addToBagTextsSelector = '[data-product-quick-buy-add-to-cart-text]'
    }
    if (this.isBundleUpsell) {
      addToBagTextsSelector = `[data-product-bundle-upsell-add-to-cart-text][data-product-id="${this.productId}"]`
    }

    const addToBagTexts = window.BAO.utils.getElements(addToBagTextsSelector)

    let backInStockButtonsSelector = '[data-product-back-in-stock]'

    if (this.isQuickBuy) {
      backInStockButtonsSelector = '[data-product-quick-buy-back-in-stock]'
    }
    if (this.isBundleUpsell) {
      backInStockButtonsSelector = `[data-product-bundle-upsell-back-in-stock][data-product-id="${this.productId}"]`
    }

    const backInStockButtons = window.BAO.utils.getElements(
      backInStockButtonsSelector,
    )

    addToBagButtons.forEach(addToBagButton => {
      this.currentVariant && this.currentVariant.available
        ? (addToBagButton.disabled = false)
        : (addToBagButton.disabled = true)

      if (addToBagButton.dataset.backInStockEnabled !== 'true') return
      addToBagButton.setAttribute(
        'aria-hidden',
        (!this.currentVariant.available).toString(),
      )
    })

    backInStockButtons.forEach(backInStockButton => {
      this.currentVariant && this.currentVariant.available
        ? (backInStockButton.disabled = true)
        : (backInStockButton.disabled = false)

      backInStockButton.setAttribute(
        'data-variant-id',
        this.currentVariant.id.toString(),
      )
      backInStockButton.setAttribute(
        'aria-hidden',
        (this.currentVariant && this.currentVariant.available).toString(),
      )
    })

    const wishlistButtons = window.BAO.utils.getElements(
      '[data-product-wishlist-button]',
    )

    addToBagTexts.forEach(addToBagText => {
      addToBagText.textContent = this.currentVariant.available
        ? addToBagText.dataset.enablePreOrders &&
          addToBagText.dataset.enablePreOrders === 'true'
          ? window.variantStrings.preOrder
          : window.variantStrings.addToCart
        : window.variantStrings.unavailable
    })

    if (backInStockButtons) {
      backInStockButtons.forEach(backInStockButtons => {
        backInStockButtons.setAttribute(
          'aria-hidden',
          this.currentVariant.available,
        )
        backInStockButtons.setAttribute(
          'data-variant-id',
          this.currentVariant.id,
        )
      })
    }

    if (wishlistButtons) {
      wishlistButtons.forEach(wishlistButton => {
        wishlistButton.setAttribute('data-variant-id', this.currentVariant.id)
      })
    }
  }

  getVariantData () {
    this.variantData =
      this.variantData ||
      JSON.parse(this.querySelector('[type="application/json"]').textContent)
    return this.variantData
  }

  get optionSelector () {
    return '[data-variant-selects-option]'
  }

  get isBundleUpsell () {
    return (
      this.dataset.section && this.dataset.section === 'product-bundle-upsell'
    )
  }

  get isQuickBuy () {
    return this.dataset.section && this.dataset.section === 'product-quickbuy'
  }

  get productId () {
    return this.dataset.productId ? parseInt(this.dataset.productId) : null
  }
}

customElements.define('variant-selects', VariantSelects)

class VariantRadios extends VariantSelects {
  // eslint-disable-next-line no-useless-constructor
  constructor () {
    super()
  }

  updateOptions () {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'))
    this.options = fieldsets.map(fieldset => {
      return Array.from(fieldset.querySelectorAll('input')).find(
        radio => radio.checked,
      ).value
    })
  }
}

customElements.define('variant-radios', VariantRadios)
